import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Loader, AlertTriangle, AlertCircle, CheckCircle, Lock,
  Info, Trash2, Mail, Pause, Play, Plus, X, LayoutGrid
} from 'lucide-react';
import config from '../../config';
import { useBulleAuSurvol } from '../BulleSurvol';
import {
  FAMILLES_DROITS, PORTEES, droitsDeLaFamille, libellesDesDroits
} from '../../constants/clientTools';
import {
  listerCartes, rattacherEmail, detacherEmail, changerActivite
} from '../../api/accesCartes';
import './FicheAccesClient.css';

/**
 * Tout ce qu'une agence décide pour **un** accès client : ses droits, et ses cartes.
 *
 * ## Pourquoi cette page existe
 *
 * L'onglet « Accès clients » posait une colonne de case à cocher par outil accordable : 1512 px
 * de largeur minimale à six outils, et 132 px de plus par droit ajouté. La liste ne porte plus
 * qu'un résumé ; le détail est ici, où la place est verticale et ne manque pas.
 *
 * ## ⚠️ Aucun identifiant de droit n'est écrit dans ce fichier
 *
 * Les sections dérivent de `FAMILLES_DROITS`, et le composant de chaque ligne de la **portée**
 * du droit (`CONTROLES_PAR_PORTEE`). C'est ce qui tient la promesse « ajouter un droit = ajouter
 * une entrée au registre » : un seul `if (droit.id === …)` ici, et le droit suivant ne
 * s'affichera pas. `backend/scripts/test-registre-droits-client.js` le vérifie.
 */

const STATUTS = {
  ACCEPTED: { bg: '#dcfce7', color: '#15803d', label: 'Connecté' },
  PENDING: { bg: '#fef3c7', color: '#b45309', label: 'En attente' },
  EXPIRED: { bg: '#fee2e2', color: '#b91c1c', label: 'Expiré' },
  ORPHAN: { bg: '#fce7f3', color: '#be185d', label: 'Orphelin' }
};

const formaterDate = (valeur) => (
  valeur ? new Date(valeur).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : null
);

/**
 * Un droit qui se règle par un interrupteur, pour toute l'organisation.
 *
 * ⚠️ **Il est désarmé pour un orphelin**, et le dit. Le serveur refuse l'octroi à un email
 * rattaché à aucune carte — c'est juste, il n'y aurait aucune donnée à montrer —, et offrir une
 * case qui répond 403 se lirait comme une panne.
 */
function BasculeOrganisation({
  droit, fiche, enCours, onBasculer, bulle, enCoursSousReglage, onBasculerDashboardSurMesure
}) {
  /* ⚠️ `CONTROLES_PAR_PORTEE` est déclaré plus bas : la lecture a lieu au **rendu**, pas à
     la déclaration de cette fonction, donc hors de la zone morte du `const`. */
  const SousReglages = droit.sousReglages ? CONTROLES_PAR_PORTEE[droit.sousReglages] : null;
  const accorde = fiche.droits.organisation.accordes.includes(droit.id);
  const utilise = fiche.droits.organisation.utilises.includes(droit.id);
  const inutilise = accorde && !utilise;
  const desarme = fiche.isOrphan || enCours;
  // La cause exacte si le serveur sait la dire, sinon la phrase du registre.
  const motifInutilise = fiche.droits.organisation.motifs?.[droit.id] || droit.siInutilise;

  return (
    <div className="kfac-droit" data-accorde={accorde ? 'oui' : 'non'}>
      <label className="kfac-droit-case">
        {enCours
          ? <Loader size={15} className="spin" style={{ color: '#9ca3af' }} />
          : (
            <input
              type="checkbox"
              checked={accorde}
              disabled={desarme}
              aria-label={droit.nom}
              onChange={(e) => onBasculer(droit.id, e.target.checked)}
            />
          )}
      </label>

      <div className="kfac-droit-corps">
        <div className="kfac-droit-titre">
          <span
            className="ksi-survolable"
            onMouseEnter={bulle.survoler(droit.description)}
            onMouseLeave={bulle.quitter}
          >
            {droit.nom}
          </span>
          {inutilise && (
            <span
              className="kfac-etiquette kfac-etiquette-alerte ksi-survolable"
              /*
               * ⚠️ **La raison, et non le constat.** « N'est utilisé pour aucun de ses
               * clients » envoie chercher du côté du rattachement, alors que la cause est
               * propre à l'outil et presque toujours ailleurs — pour les dashboards sur
               * mesure, c'est le marquage « visible par le client » qui manque. Le registre
               * la porte, droit par droit.
               */
              /*
               * ⚠️ **Le motif du serveur l'emporte sur la phrase du registre.** Celle-ci est
               * écrite d'avance et ne peut nommer qu'une cause ; or ce droit en a trois, et
               * une phrase figée se trompe donc deux fois sur trois. Le serveur, lui, a fait
               * les requêtes : il sait laquelle. La phrase reste en repli, pour les outils
               * dont la condition est unique.
               */
              onMouseEnter={bulle.survoler(
                `« ${droit.nom} » ne sera pas affiché à ce client : l'outil n'ouvrirait rien.`
                + (motifInutilise ? `\n\n${motifInutilise}` : ''))}
              onMouseLeave={bulle.quitter}
            >
              <AlertTriangle size={11} /> inutilisé
            </span>
          )}
        </div>
        <div className="kfac-droit-ou">Il le trouve dans : {droit.ouvrePar}</div>

        {/*
          ⚠️ **Le second cran, sous la case du premier.** Un droit peut n'ouvrir l'écran que
          si un autre réglage suit — pour les dashboards sur mesure, le marquage de chaque
          fiche. Les séparer, c'est ce qui faisait lire « inutilisé » sans savoir où aller.
        */}
        {SousReglages && (
          <SousReglages droit={droit} fiche={fiche} enCours={enCoursSousReglage}
            onBasculerDashboardSurMesure={onBasculerDashboardSurMesure} bulle={bulle} />
        )}
      </div>
    </div>
  );
}

/**
 * Les dashboards de plateforme : un interrupteur par régie et par solution de mesure.
 *
 * ⚠️ **Tout est coché par défaut, et le sens est inversé.** Ces écrans sont visibles depuis
 * toujours ; une ligne en base n'existe que pour un dashboard **retiré**. Décocher écrit,
 * recocher efface — et non l'inverse. C'est pour cela que la liste reçoit `retires` et non
 * `ouverts` : énumérer les autorisations obligerait à connaître le catalogue ici, et une
 * plateforme ajoutée demain paraîtrait fermée.
 *
 * ⚠️ **Seules les plateformes réellement présentes sont montrées.** Un interrupteur TikTok sur
 * un accès dont aucun client n'a de compte TikTok laisserait croire qu'un réglage est en jeu
 * là où il n'y a rien à voir. Le serveur fait ce tri, pour la même raison qu'il calcule le
 * verdict d'usage des outils.
 */
function BasculesParPlateforme({ droit, fiche, enCours, onBasculerDashboard, bulle }) {
  const { ouvrables = [], retires = [] } = fiche.droits.parPlateforme || {};

  return (
    <div className="kfac-droit kfac-droit-groupe">
      <span className="kfac-droit-case"><LayoutGrid size={15} style={{ color: '#6b7280' }} /></span>

      <div className="kfac-droit-corps">
        <div className="kfac-droit-titre">
          <span
            className="ksi-survolable"
            onMouseEnter={bulle.survoler(droit.description)}
            onMouseLeave={bulle.quitter}
          >
            {droit.nom}
          </span>
        </div>
        <div className="kfac-droit-ou">Il les trouve dans : {droit.ouvrePar}</div>

        {ouvrables.length === 0 ? (
          <div className="kfac-plateformes-vide">
            Aucun dashboard à régler : les clients rattachés n'ont pas de compte consultable.
          </div>
        ) : (
          <div className="kfac-plateformes">
            {ouvrables.map(plateforme => {
              const ouvert = !retires.includes(plateforme.id);
              const occupe = enCours === plateforme.id;

              return (
                <label key={plateforme.id} className="kfac-plateforme" data-ouvert={ouvert ? 'oui' : 'non'}>
                  {occupe ? (
                    <Loader size={13} className="spin" style={{ color: '#9ca3af' }} />
                  ) : (
                    <input
                      type="checkbox"
                      checked={ouvert}
                      disabled={fiche.isOrphan}
                      onChange={(e) => onBasculerDashboard(plateforme.id, e.target.checked)}
                    />
                  )}
                  <span>{plateforme.libelle}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Les dashboards sur mesure atteignables, et leur marquage « visible par le client ».
 *
 * ⚠️ **Ce marquage n'appartient pas à cet accès, et l'écran doit le dire.** C'est un drapeau
 * de la **fiche de dashboard** : le lever l'ouvre à *tous* les accès clients des comptes
 * concernés, pas au seul qu'on regarde. Le montrer ici est juste — c'est le second cran sans
 * lequel le droit n'ouvre rien, et aller le chercher dans un autre écran après avoir lu
 * « inutilisé » coûtait plusieurs allers-retours — mais le présenter comme un réglage propre
 * à cette personne serait faux. D'où la phrase, qui n'est pas décorative.
 */
function ListeParDashboard({ droit, fiche, enCours, onBasculerDashboardSurMesure }) {
  const fiches = fiche.droits.parDashboard || [];
  const accorde = fiche.droits.organisation.accordes.includes(droit.id);

  return (
    <div className="kfac-sous-reglage">
      <div className="kfac-droit-corps">
        {fiches.length === 0 ? (
          <div className="kfac-plateformes-vide">
            Aucun dashboard sur mesure sur les comptes de ses clients.
          </div>
        ) : (
          <>
            <div className="kfac-droit-ou">
              Cochez ce qu'il peut ouvrir. ⚠️ Ce marquage vaut pour <strong>tous</strong> les
              accès clients de ces comptes, pas seulement celui-ci.
              {!accorde && " Le droit ci-dessus doit aussi être coché : il en faut les deux."}
            </div>

            <ul className="kfac-fiches">
              {fiches.map(f => {
                const occupe = enCours === f.id;
                return (
                  <li key={f.id} data-ouvert={f.visibleClient ? 'oui' : 'non'}>
                    <label>
                      {occupe ? (
                        <Loader size={13} className="spin" style={{ color: '#9ca3af' }} />
                      ) : (
                        <input
                          type="checkbox"
                          checked={f.visibleClient}
                          /* ⚠️ Une fiche inactive ne s'ouvre pas, même marquée visible : la
                             cocher laisserait croire le contraire. */
                          disabled={fiche.isOrphan || !f.actif}
                          onChange={(e) => onBasculerDashboardSurMesure(f.id, e.target.checked)}
                        />
                      )}
                      <span className="kfac-fiche-nom">{f.nom}</span>
                    </label>
                    <span className="kfac-fiche-compte">
                      {f.compte}
                      {!f.actif && <span className="kfac-etiquette">inactif</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Les deux portées à venir — un réglage par carte, un réglage par fiche de dashboard.
 *
 * Déclarées maintenant plutôt qu'au moment de les remplir : c'est la table des portées qui dit
 * ce que la page sait rendre, et une portée absente ferait rendre `undefined` comme composant.
 * Ici, le droit paraît avec la mention de ce qui lui manque, ce qui vaut mieux qu'une section
 * qui disparaît sans un mot.
 */
function PorteeAVenir({ droit, bulle }) {
  return (
    <div className="kfac-droit kfac-droit-avenir">
      <span className="kfac-droit-case"><Info size={15} style={{ color: '#9ca3af' }} /></span>
      <div className="kfac-droit-corps">
        <div className="kfac-droit-titre">
          <span
            className="ksi-survolable"
            onMouseEnter={bulle.survoler(droit.description)}
            onMouseLeave={bulle.quitter}
          >
            {droit.nom}
          </span>
        </div>
        <div className="kfac-droit-ou">Ce réglage n'est pas encore repris ici.</div>
      </div>
    </div>
  );
}

/**
 * ⚠️ Une table, jamais un `switch`.
 *
 * Un `switch` se complète en ajoutant une branche, ce qui est précisément le geste qu'on veut
 * rendre inutile. Avec une table, une portée déclarée dans le registre et absente d'ici est
 * attrapée par le contrôle, pas découverte à l'écran.
 */
const CONTROLES_PAR_PORTEE = {
  [PORTEES.ORGANISATION]: BasculeOrganisation,
  [PORTEES.PAR_PLATEFORME]: BasculesParPlateforme,
  [PORTEES.PAR_CARTE]: PorteeAVenir,
  // Sert de sous-réglage, jamais de portée principale : le droit reste un `ClientToolAccess`.
  [PORTEES.PAR_DASHBOARD]: ListeParDashboard
};

function FicheAccesClient({ token, orgId, email, onRetour }) {
  const [fiche, setFiche] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [droitEnCours, setDroitEnCours] = useState(null);
  const [dashboardEnCours, setDashboardEnCours] = useState(null);
  const [dashboardSurMesureEnCours, setDashboardSurMesureEnCours] = useState(null);
  const [carteEnCours, setCarteEnCours] = useState(null);
  const [cartesDisponibles, setCartesDisponibles] = useState(null);
  const [ajoutOuvert, setAjoutOuvert] = useState(false);
  const [carteChoisie, setCarteChoisie] = useState('');
  const bulle = useBulleAuSurvol();

  const entetes = { Authorization: `Bearer ${token}` };

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      /* ⚠️ L'email est encodé : un « + » non encodé revient en espace côté serveur, et la
         fiche répondrait 404 sur un accès qui existe. */
      const reponse = await fetch(
        `${config.API_URL}/api/organizations/${orgId}/client-accesses/fiche`
        + `?email=${encodeURIComponent(email)}`,
        { headers: entetes }
      );
      const donnees = await reponse.json();
      if (donnees.success) setFiche(donnees.acces);
      else setErreur(donnees.error || "Cet accès client n'a pas pu être chargé");
    } catch {
      setErreur('Erreur de connexion');
    } finally {
      setChargement(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, email, token]);

  useEffect(() => { charger(); }, [charger]);

  /**
   * Ouvre ou ferme un droit.
   *
   * La réponse porte la liste à jour des droits **et** de leur usage : la reprendre évite de
   * recharger la fiche, et c'est ce qui fait apparaître la mention « inutilisé » tout de suite.
   */
  const basculerDroit = async (toolId, accorde) => {
    setDroitEnCours(toolId);
    setErreur('');
    try {
      const reponse = await fetch(
        `${config.API_URL}/api/organizations/${orgId}/client-accesses/tools/${toolId}`,
        {
          method: 'PUT',
          headers: { ...entetes, 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: fiche.email, granted: accorde })
        }
      );
      const donnees = await reponse.json();
      if (donnees.success) {
        setFiche(precedent => ({
          ...precedent,
          droits: {
            ...precedent.droits,
            organisation: { accordes: donnees.tools, utilises: donnees.toolsInUse || [] }
          }
        }));
      } else {
        setErreur(donnees.error || "Ce droit n'a pas pu être modifié");
      }
    } catch {
      setErreur('Erreur de connexion');
    } finally {
      setDroitEnCours(null);
    }
  };

  /**
   * Ouvre ou ferme un dashboard de plateforme.
   *
   * ⚠️ **Le booléen est inversé côté base, et la route s'en charge seule.** Ici `ouvert: true`
   * veut dire ce qu'il dit ; c'est la route qui traduit en suppression de la ligne de retrait.
   * Inverser des deux côtés donnerait un interrupteur qui semble marcher et ne fait rien.
   */
  const basculerDashboard = async (dashboardId, ouvert) => {
    setDashboardEnCours(dashboardId);
    setErreur('');
    try {
      const reponse = await fetch(
        `${config.API_URL}/api/organizations/${orgId}/client-accesses/dashboards/${dashboardId}`,
        {
          method: 'PUT',
          headers: { ...entetes, 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: fiche.email, granted: ouvert })
        }
      );
      const donnees = await reponse.json();
      if (donnees.success) {
        // La réponse porte la liste à jour des retraits : la reprendre évite de relire la
        // fiche entière, comme le fait déjà la bascule d'un droit d'outil.
        setFiche(precedent => ({
          ...precedent,
          droits: {
            ...precedent.droits,
            parPlateforme: {
              ...precedent.droits.parPlateforme,
              retires: donnees.dashboardsRetires
            }
          }
        }));
      } else {
        setErreur(donnees.error || "Ce dashboard n'a pas pu être modifié");
      }
    } catch {
      setErreur('Erreur de connexion');
    } finally {
      setDashboardEnCours(null);
    }
  };

  /**
   * Marque une fiche de dashboard sur mesure visible, ou non, par les accès clients.
   *
   * ⚠️ **La route des dashboards sur mesure, et non une route nouvelle.** C'est elle qui
   * valide la fiche entière avant d'écrire ; s'en donner une seconde ici, c'est oublier ce
   * contrôle — et l'oubli ne se verrait pas, l'écriture marchant.
   */
  const basculerDashboardSurMesure = async (idFiche, visible) => {
    setDashboardSurMesureEnCours(idFiche);
    setErreur('');
    try {
      const reponse = await fetch(`${config.API_URL}/api/dashboards-sur-mesure/${idFiche}`, {
        method: 'PUT',
        headers: { ...entetes, 'Content-Type': 'application/json' },
        body: JSON.stringify({ visibleClient: visible })
      });
      const donnees = await reponse.json();
      if (donnees.success) {
        /*
         * ⚠️ On relit la fiche entière, et non la seule ligne : le verdict « inutilisé » du
         * droit dépend de ce marquage. Le mettre à jour sur place laisserait la pastille
         * mentir jusqu'au prochain chargement.
         */
        await charger();
      } else {
        setErreur(donnees.error || "Ce dashboard n'a pas pu être modifié");
      }
    } catch {
      setErreur('Erreur de connexion');
    } finally {
      setDashboardSurMesureEnCours(null);
    }
  };

  /**
   * Les cartes qu'on peut encore rattacher : celles de l'organisation, moins les siennes.
   *
   * ⚠️ **La liste vient de la route des cartes, pas de la fiche.** Les deux n'ont pas le même
   * périmètre — la fiche voit les cartes de tous les membres, cette route celles du seul
   * propriétaire des données. Offrir ce que la fiche affiche ferait donc proposer une carte dont
   * le rattachement répondrait 404. Offrir moins est honnête ; promettre et échouer, non.
   */
  const chargerCartesDisponibles = async () => {
    if (cartesDisponibles) return;
    try {
      const cartes = await listerCartes(token);
      setCartesDisponibles(cartes);
    } catch {
      setCartesDisponibles([]);
      setErreur("La liste des clients n'a pas pu être chargée");
    }
  };

  /**
   * Après une écriture sur les cartes, la fiche se relit **entière**.
   *
   * Ces routes ne rendent pas de ligne recalculée, et le statut, l'état d'orphelin et l'usage
   * des droits dépendent tous des cartes : détacher la dernière carte rend l'accès orphelin et
   * doit fermer toutes les bascules. Recalculer cela dans le navigateur se tromperait au premier
   * cas tordu.
   */
  const apresEcritureCarte = async (texte) => {
    setMessage(texte);
    setCartesDisponibles(null);
    setAjoutOuvert(false);
    setCarteChoisie('');
    await charger();
  };

  const executerSurCarte = async (cle, action, texte) => {
    setCarteEnCours(cle);
    setErreur('');
    setMessage('');
    try {
      await action();
      await apresEcritureCarte(texte);
    } catch (e) {
      setErreur(e.message || "Ce geste n'a pas pu être effectué");
    } finally {
      setCarteEnCours(null);
    }
  };

  if (chargement) {
    return (
      <div className="kfac-page kfac-centre">
        <Loader size={22} className="spin" style={{ color: '#9ca3af' }} />
        <span>Chargement de l'accès client…</span>
      </div>
    );
  }

  if (!fiche) {
    return (
      <div className="kfac-page">
        <button className="kfac-retour" onClick={onRetour}>
          <ArrowLeft size={15} /> Accès clients
        </button>
        <div className="kfac-alerte kfac-alerte-erreur">
          <AlertCircle size={15} />
          {erreur || "Cet accès client n'existe pas, ou il a été supprimé depuis."}
        </div>
      </div>
    );
  }

  const statut = STATUTS[fiche.globalStatus] || STATUTS.PENDING;
  const accordesAilleurs = fiche.droits.ailleurs?.toolIds || [];
  const cartesPrises = new Set((fiche.clientCards || []).map(c => c.clientId));
  const offrables = (cartesDisponibles || []).filter(c => !cartesPrises.has(c.id));

  return (
    <div className="kfac-page">
      <button className="kfac-retour" onClick={onRetour}>
        <ArrowLeft size={15} /> Accès clients
      </button>

      {/* Identité */}
      <header className="kfac-entete">
        {fiche.picture
          ? <img src={fiche.picture} alt="" className="kfac-portrait" />
          : <div className="kfac-portrait kfac-portrait-initiale">
              {(fiche.name || fiche.email).charAt(0).toUpperCase()}
            </div>}
        <div className="kfac-identite">
          <h1>{fiche.email}</h1>
          <div className="kfac-sous-titre">
            {fiche.name || 'Sans nom'}
            {fiche.isGoogleEmail && <span className="kfac-discret"> · Google</span>}
            {fiche.lastLoginAt
              ? <span className="kfac-discret"> · vu le {formaterDate(fiche.lastLoginAt)}</span>
              : <span className="kfac-discret"> · jamais connecté</span>}
          </div>
        </div>
        <span className="kfac-statut" style={{ backgroundColor: statut.bg, color: statut.color }}>
          {statut.label}
        </span>
      </header>

      {erreur && <div className="kfac-alerte kfac-alerte-erreur"><AlertCircle size={15} />{erreur}</div>}
      {message && <div className="kfac-alerte kfac-alerte-succes"><CheckCircle size={15} />{message}</div>}

      {/* ⚠️ Un orphelin ne se règle pas : il s'explique. */}
      {fiche.isOrphan && (
        <div className="kfac-alerte kfac-alerte-avis">
          <AlertTriangle size={15} />
          <span>
            Cet accès n'est rattaché à <strong>aucun client</strong>, ici ni ailleurs. Il
            n'a donc accès à aucune donnée, et aucun droit ne peut lui être ouvert : rattachez-le
            d'abord à un client.
          </span>
        </div>
      )}

      {/*
        Les clients d'abord, et les droits ensuite.

        ⚠️ **Le rattachement commande tout le reste** : un droit n'ouvre que les comptes des
        cartes de cet accès, et un accès sans carte ne voit rien — c'est ce que dit l'avis
        juste au-dessus. Présenter les droits avant le périmètre auquel ils s'appliquent
        faisait lire les cases comme si elles suffisaient.
      */}
      <section className="kfac-bloc">
        <div className="kfac-bloc-entete kfac-bloc-entete-action">
          <div>
            <h2>Clients</h2>
            <p>Les clients de l'agence dont il voit les comptes. C'est ce rattachement qui
              définit son périmètre.</p>
          </div>
          <button
            className="kfac-bouton"
            onClick={() => { setAjoutOuvert(!ajoutOuvert); chargerCartesDisponibles(); }}
          >
            {ajoutOuvert ? <X size={14} /> : <Plus size={14} />}
            {ajoutOuvert ? 'Annuler' : 'Rattacher un client'}
          </button>
        </div>

        {ajoutOuvert && (
          <div className="kfac-ajout">
            {cartesDisponibles === null ? (
              <span className="kfac-discret"><Loader size={13} className="spin" /> Chargement des clients…</span>
            ) : offrables.length === 0 ? (
              <span className="kfac-discret">
                Aucun autre client à rattacher. Les clients se créent depuis l'écran Clients.
              </span>
            ) : (
              <>
                <select value={carteChoisie} onChange={(e) => setCarteChoisie(e.target.value)}>
                  <option value="">Choisir un client…</option>
                  {offrables.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button
                  className="kfac-bouton kfac-bouton-plein"
                  disabled={!carteChoisie || carteEnCours === 'ajout'}
                  onClick={() => executerSurCarte(
                    'ajout',
                    () => rattacherEmail(token, carteChoisie, fiche.email),
                    `${fiche.email} a été rattaché.`
                  )}
                >
                  {carteEnCours === 'ajout'
                    ? <Loader size={14} className="spin" />
                    : <Mail size={14} />}
                  Rattacher
                </button>
                <span className="kfac-discret">
                  {/* ⚠️ Ni validation ni attente : l'accès est ouvert dès l'enregistrement,
                      et la personne en est seulement informée. Parler d'invitation laissait
                      croire à une acceptation nécessaire. */}
                  La personne en est informée par courriel ; son accès est ouvert aussitôt.
                </span>
              </>
            )}
          </div>
        )}

        {(fiche.clientCards || []).length === 0 ? (
          <div className="kfac-vide">Aucun client rattaché.</div>
        ) : (
          <ul className="kfac-cartes">
            {fiche.clientCards.map(carte => {
              const occupe = carteEnCours === carte.clientEmailId;

              return (
                <li key={carte.clientEmailId} data-suspendu={carte.isActive ? 'non' : 'oui'}>
                  <div className="kfac-carte-nom">
                    {carte.clientName}
                    {/*
                      ⚠️ **Aucun badge d'invitation par ligne, et c'est délibéré.**

                      Il affichait `ClientInvitation.status`, qui ne passe à « accepté » qu'en
                      **suivant le lien reçu**. Une personne déjà connectée ne repasse jamais
                      par là : chaque client qu'on lui rattache naissait « En attente » et y
                      restait, juste sous un en-tête qui la dit « Connecté ». Deux badges, deux
                      sources, et celui de la ligne avait tort.

                      Le statut décrit la **personne**, pas le rattachement : il est en tête,
                      une fois. Ce que la ligne porte, c'est ce qui lui appartient vraiment —
                      « suspendu ».
                    */}
                    {!carte.isActive && <span className="kfac-etiquette">suspendu</span>}
                  </div>

                  <div className="kfac-carte-gestes">
                    {occupe ? <Loader size={14} className="spin" style={{ color: '#9ca3af' }} /> : (
                      <>
                        {/*
                          ⚠️ **Pas de « Relancer » ici.** Rattacher une adresse à un client
                          n'attend aucune validation : la personne reçoit un courriel qui l'en
                          informe, et son accès est ouvert dès l'enregistrement. Proposer une
                          relance laissait croire à une invitation en suspens — et le statut
                          « En attente » qui l'accompagnait désignait en réalité une personne
                          qui ne s'est simplement pas encore connectée.
                        */}
                        {/* Suspendre plutôt que détacher : le rattachement reste, l'accès se ferme.
                            C'est le geste réversible, et il se distingue du détachement. */}
                        <button
                          className="kfac-geste"
                          title={carte.isActive
                            ? "Fermer l'accès à ce client sans défaire le rattachement"
                            : "Rouvrir l'accès à ce client"}
                          onClick={() => executerSurCarte(
                            carte.clientEmailId,
                            () => changerActivite(token, carte.clientId, carte.clientEmailId, !carte.isActive),
                            carte.isActive ? 'Accès suspendu.' : 'Accès rétabli.'
                          )}
                        >
                          {carte.isActive ? <><Pause size={14} /> Suspendre</> : <><Play size={14} /> Rétablir</>}
                        </button>
                        <button
                          className="kfac-geste kfac-geste-danger"
                          title="Retirer cette adresse de ce client"
                          onClick={() => {
                            if (!window.confirm(
                              `Détacher ${fiche.email} de « ${carte.clientName} » ?\n\n`
                              + "Il perdra l'accès aux comptes de ce client. Son compte et ses "
                              + 'autres rattachements ne sont pas touchés.')) return;
                            executerSurCarte(
                              carte.clientEmailId,
                              () => detacherEmail(token, carte.clientId, carte.clientEmailId),
                              'Client détaché.'
                            );
                          }}
                        >
                          <Trash2 size={14} /> Détacher
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Les droits, famille par famille */}
      {FAMILLES_DROITS.map(famille => {
        const droits = droitsDeLaFamille(famille.id);
        if (droits.length === 0) return null;

        return (
          <section className="kfac-bloc" key={famille.id}>
            <div className="kfac-bloc-entete">
              <h2>{famille.nom}</h2>
              <p>{famille.description}</p>
            </div>
            <div className="kfac-droits">
              {droits.map(droit => {
                const Controle = CONTROLES_PAR_PORTEE[droit.portee];
                return (
                  <Controle
                    key={droit.id}
                    droit={droit}
                    fiche={fiche}
                    bulle={bulle}
                    enCours={droit.portee === PORTEES.PAR_PLATEFORME
                      ? dashboardEnCours
                      : droitEnCours === droit.id}
                    enCoursSousReglage={dashboardSurMesureEnCours}
                    onBasculer={basculerDroit}
                    onBasculerDashboard={basculerDashboard}
                    onBasculerDashboardSurMesure={basculerDashboardSurMesure}
                  />
                );
              })}
            </div>
          </section>
        );
      })}

      {/*
        ⚠️ Les droits qu'une autre organisation a accordés sont montrés, sans la nommer.

        Les taire serait un mensonge actif : les droits d'un client sont cherchés par email, sans
        filtre d'organisation, donc une agence qui décoche un droit ici le verrait conservé et
        conclurait à une panne. Mais nommer l'agence tierce serait une information commerciale
        qu'aucune des deux n'a consenti à partager.
      */}
      {accordesAilleurs.length > 0 && (
        <section className="kfac-bloc kfac-bloc-ailleurs">
          <div className="kfac-bloc-entete">
            <h2><Lock size={14} /> Accordé par une autre organisation</h2>
            <p>
              Cette adresse est aussi cliente ailleurs
              {fiche.droits.ailleurs.nombreOrganisations > 1
                ? ` (${fiche.droits.ailleurs.nombreOrganisations} organisations)` : ''}.
              Vous ne pouvez ni retirer ni modifier ces droits — ils continueront de s'appliquer.
            </p>
          </div>
          <ul className="kfac-liste-ailleurs">
            {libellesDesDroits(accordesAilleurs).map(nom => <li key={nom}>{nom}</li>)}
          </ul>
        </section>
      )}

      {bulle.bulle}
    </div>
  );
}

export default FicheAccesClient;
